import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { Card, Icon, SquareDotLoadingIndicator } from '@joor/design-system'
import styled from 'styled-components'

import ProductImage from './ProductImage'
import { useProductImagesProps } from './productImages.hooks'
import { ProductImagesIds } from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.ids'
import messages from 'features/Linesheets/Modals/ManageImagesModal/manageImagesModal.messages'
import useStore from 'features/Linesheets/Modals/ManageImagesModal/store/useManageImagesStore'

const AppendImagePlaceholder = styled.div`
  width: 52px;
  height: 88px;
  background-color: var(--color-secondary-100);
  border: 1px dashed var(--color-primary-200);
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  &:not(.disabled) {
    cursor: pointer;

    &:hover span svg {
      --main-color: var(--color-secondary-600);
    }
  }

  &.disabled {
    cursor: not-allowed;

    span svg {
      --main-color: var(--color-primary-200);

      &:hover {
        --main-color: var(--color-primary-200);
      }
    }
  }
`

const StyledCard = styled(Card)`
  flex: 1;

  > header {
    max-width: 100%;
    background-color: var(--color-neutral-000-full);
    padding-bottom: 0;
  }

  &:not(.loading) > section {
    display: grid;
    flex: auto;
    gap: 8px;
    width: fit-content;
    overflow: auto;
    grid-template-rows: min-content;
    grid-template-columns: repeat(4, 1fr);

    @media (min-width: 1120px) {
      grid-template-columns: repeat(5, 1fr);
    }

    @media (min-width: 1360px) {
      grid-template-columns: repeat(6, 1fr);
    }
  }

  &.loading {
    position: relative;
    width: auto;
    height: auto;

    & > section {
      min-height: 64px;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  }
`
const ProductImages = (): JSX.Element => {
  const isStyleDataLoading = useStore((state) => state.isStyleDataLoading)
  const {
    areActionsDisabled,
    canAssignImages,
    canUploadImage,
    productImages,
    sensors,
    selectedImages,
    handleAssignImages,
    handleInputEvent,
    getInputProps,
    getRootProps,
    onDragEnd,
  } = useProductImagesProps()

  return (
    <StyledCard
      testId={ProductImagesIds.Card}
      title={`${messages.productImages}${
        productImages ? ` (${productImages.length})` : ''
      }`}
      className={isStyleDataLoading ? 'loading' : ''}
      actionButtonId={ProductImagesIds.AssignImagesAction}
      actionButtonTestId={ProductImagesIds.AssignImagesAction}
      actionButtonLabel={
        canAssignImages && !areActionsDisabled
          ? messages.assignNImages.replace(
              ':amount',
              selectedImages.size.toString(),
            )
          : undefined
      }
      onClickActionButton={
        canAssignImages && !areActionsDisabled ? handleAssignImages : undefined
      }
    >
      {!isStyleDataLoading ? (
        <>
          {!!productImages.length && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onDragEnd}
            >
              <SortableContext
                disabled={areActionsDisabled}
                items={productImages.map(
                  (image) =>
                    (image.unmatchedUploadId as string) || (image.id as string),
                )}
              >
                {productImages.map((image) => (
                  <ProductImage
                    key={
                      (image.unmatchedUploadId as string) ||
                      (image.id as string)
                    }
                    image={image}
                    areActionsDisabled={areActionsDisabled}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
          {!!canUploadImage && (
            <span
              {...getRootProps({
                onDrop: (e) => handleInputEvent(e),
                onClick: (e) => handleInputEvent(e),
              })}
            >
              <AppendImagePlaceholder
                data-testid={ProductImagesIds.UploadImageButton}
                className={areActionsDisabled ? 'disabled' : ''}
                title="Upload"
              >
                <Icon iconName="upload" />
              </AppendImagePlaceholder>
              <input {...getInputProps()} />
            </span>
          )}
          {!!canAssignImages && (
            <AppendImagePlaceholder
              data-testid={ProductImagesIds.AssignImagesButton}
              className={areActionsDisabled ? 'disabled' : ''}
              onClick={handleAssignImages}
              title="Assign"
            >
              <Icon iconName="add" />
            </AppendImagePlaceholder>
          )}
        </>
      ) : (
        <SquareDotLoadingIndicator />
      )}
    </StyledCard>
  )
}

export default ProductImages
